import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://taryfypradu.pl';
const SITE_NAME = 'Taryfy Pradu';

/** Strip trailing slashes from paths (preserves root '/') */
function normalizePath(path: string): string {
  return path === '/' ? path : path.replace(/\/+$/, '');
}

interface UseSEOOptions {
  /** Page title (will be suffixed with " | Taryfy Pradu") */
  title: string;
  /** Meta description for search engines */
  description?: string;
  /** Canonical URL path (e.g., "/kontakt"). If not provided, uses current pathname. */
  canonicalPath?: string;
  /** Set to true for pages that should NOT be indexed (auth, private pages) */
  noindex?: boolean;
}

/**
 * Comprehensive SEO hook for managing document head tags.
 *
 * Dynamically sets:
 * - <title>
 * - <meta name="description">
 * - <link rel="canonical">
 * - <meta name="robots"> (noindex/nofollow for private pages)
 *
 * Cleans up on unmount to restore previous values.
 */
export function useSEO({ title, description, canonicalPath, noindex }: UseSEOOptions) {
  const location = useLocation();

  // --- Document title ---
  useEffect(() => {
    const prev = document.title;
    document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    return () => { document.title = prev; };
  }, [title]);

  // --- Meta description ---
  useEffect(() => {
    const tag = getOrCreateMeta('description');
    const prev = tag.getAttribute('content') || '';

    if (description) {
      tag.setAttribute('content', description);
    }

    return () => { tag.setAttribute('content', prev); };
  }, [description]);

  // --- Canonical URL ---
  useEffect(() => {
    // Use provided canonical path, or derive from current location (strip query/hash)
    const path = normalizePath(canonicalPath ?? location.pathname);
    const href = `${BASE_URL}${path}`;

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    const prev = link.href;
    link.href = href;
    return () => { link!.href = prev; };
  }, [canonicalPath, location.pathname]);

  // --- Robots meta (noindex) ---
  useEffect(() => {
    if (!noindex) {
      // Remove any noindex tag we may have added
      const existing = document.querySelector('meta[name="robots"][data-seo-hook]');
      if (existing) existing.remove();
      return;
    }

    // Add noindex tag
    const tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    tag.setAttribute('content', 'noindex, nofollow');
    tag.setAttribute('data-seo-hook', 'true');
    document.head.appendChild(tag);

    return () => { tag.remove(); };
  }, [noindex]);
}

/** Get or create a <meta name="..."> tag */
function getOrCreateMeta(name: string): HTMLMetaElement {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  return tag;
}
